// GTM section — DUMMY DATA ONLY. No backend, no fetch.
// Shape mirrors the future Mongo model so swapping to real fetches later is
// one line per page (replace these exports with an axios GET of the same shape).
//
//   campaign: { id, name, source, persona, prospectCount, status,
//               budget, estimatedCost, sampleOpeners[] }
//     source: 'manual' | 'genesis'
//     persona: 'CHRO' | 'WELLBEING'
//     status: 'pending_approval' | 'active' | 'held' | 'paused' | 'done'
//     budget        = the cap the user set for this run
//     estimatedCost = derived (see PRICING_CONFIG below), NOT a final price
//   contact: { email, firstName, lastName, companyName, persona, status, personalization, heldReason }
//   controls: { paused, breaker: { tripped, reason } }

// Per-contact rates the AI multiplies by list size to estimate a run's cost.
// These are dummy numbers; in production they come from the live rate card.
export const PRICING_CONFIG = {
  revealPerContact: 0.3, // unlocking a verified email/phone
  verifyPerContact: 0.1, // bounce/deliverability check
  researchPerContact: 1.2, // gathering the real signals an opener is built from
  draftPerContact: 0.9, // writing the personalized first lines
};

// Sum of all per-contact rates. estimatedCost = PER_CONTACT * prospectCount.
const PER_CONTACT =
  PRICING_CONFIG.revealPerContact +
  PRICING_CONFIG.verifyPerContact +
  PRICING_CONFIG.researchPerContact +
  PRICING_CONFIG.draftPerContact;

// Raw campaigns. estimatedCost is computed below so the number is visibly
// derived from PRICING_CONFIG, not hand-typed magic.
const RAW_CAMPAIGNS = [
  {
    id: 'cmp-chro-pharma-q3',
    name: 'CHRO · Mid-market Pharma',
    source: 'genesis',
    persona: 'CHRO',
    prospectCount: 412,
    status: 'active',
    budget: 4800,
    sampleOpeners: [
      'Saw Veridian just stood up a 2,000-person hybrid policy — curious how your EX team is measuring adoption without another survey tool.',
      'Most CHROs we talk to are drowning in point solutions. We collapse benefits, wellbeing, and coordination into one orchestration layer.',
    ],
  },
  {
    id: 'cmp-wellbeing-tech',
    name: 'Wellbeing · Series C Tech',
    source: 'manual',
    persona: 'WELLBEING',
    prospectCount: 188,
    status: 'pending_approval',
    budget: 2600,
    sampleOpeners: [
      'Your wellbeing budget probably went up 30% and engagement stayed flat — that gap is exactly what we close.',
      'We replaced three vendors for a 900-person eng org and lifted weekly active wellbeing usage from 11% to 38%.',
    ],
  },
  {
    id: 'cmp-chro-fintech',
    name: 'CHRO · Enterprise Fintech',
    source: 'genesis',
    persona: 'CHRO',
    prospectCount: 95,
    status: 'pending_approval',
    budget: 5200,
    sampleOpeners: [
      'RTO mandates are landing hard in fintech — we give CHROs a coordination layer so the policy actually sticks instead of churning people.',
      'You ran a reorg last quarter. The hidden cost is coordination overhead; we quantify it and then remove it.',
    ],
  },
  {
    id: 'cmp-wellbeing-retail',
    name: 'Wellbeing · National Retail',
    source: 'manual',
    persona: 'WELLBEING',
    prospectCount: 540,
    status: 'held',
    budget: 3100,
    sampleOpeners: [
      'Frontline wellbeing is impossible to measure with a desktop tool — ours meets associates where they already are.',
      'Turnover on the floor is a wellbeing problem before it is a comp problem. Here is what we changed for a 12k-store operator.',
    ],
  },
  {
    id: 'cmp-chro-manufacturing',
    name: 'CHRO · Industrial Manufacturing',
    source: 'genesis',
    persona: 'CHRO',
    prospectCount: 233,
    status: 'paused',
    budget: 2900,
    sampleOpeners: [
      'Shift-based workforces break every HR tool built for knowledge workers. We were built for both.',
      'Your safety and wellbeing data live in different systems. We join them so you can see the leading indicators.',
    ],
  },
  {
    id: 'cmp-wellbeing-healthcare',
    name: 'Wellbeing · Regional Healthcare',
    source: 'manual',
    persona: 'WELLBEING',
    prospectCount: 167,
    status: 'done',
    budget: 3400,
    sampleOpeners: [
      'Clinician burnout is the wellbeing story of the decade — we instrument it without adding to the documentation load.',
      'Closed a 6-hospital system last quarter. Happy to walk through the rollout.',
    ],
  },
];

// Derive estimatedCost = round(per-contact total * list size) for each campaign.
export const CAMPAIGNS = RAW_CAMPAIGNS.map((c) => ({
  ...c,
  estimatedCost: Math.round(PER_CONTACT * c.prospectCount),
}));

export const CONTACTS = [
  {
    email: 'd.okafor@veridianpharma.com',
    firstName: 'Dana',
    lastName: 'Okafor',
    companyName: 'Veridian Pharma',
    persona: 'CHRO',
    status: 'held',
    personalization: 'Referenced the 2,000-person hybrid policy rollout from their Q2 town hall.',
    heldReason: 'Competitor domain detected in reply-to header — possible forwarded mailbox.',
  },
  {
    email: 'marcus.lin@northstar-retail.com',
    firstName: 'Marcus',
    lastName: 'Lin',
    companyName: 'Northstar Retail',
    persona: 'WELLBEING',
    status: 'held',
    personalization: 'Tied opener to their 12,000-store frontline turnover problem.',
    heldReason: 'Opener referenced a layoff rumor we could not verify — flagged by the safety stop-gap.',
  },
  {
    email: 'p.castellanos@meridianfin.com',
    firstName: 'Priya',
    lastName: 'Castellanos',
    companyName: 'Meridian Financial',
    persona: 'CHRO',
    status: 'held',
    personalization: 'Mentioned their recent reorg and coordination overhead.',
    heldReason: 'Contact is on the global do-not-contact suppression list.',
  },
];

export const CONTROLS = {
  paused: false,
  breaker: { tripped: false, reason: '' },
};

// Badge tone per campaign status. Consumed by the Outbound page.
//   active=mint, pending_approval=amber, held=pink, paused=gray, done=muted
export const STATUS_TONE = {
  active: 'mint',
  pending_approval: 'amber',
  held: 'pink',
  paused: 'gray',
  done: 'muted',
};

export const STATUS_LABEL = {
  active: 'Active',
  pending_approval: 'Pending approval',
  held: 'Held',
  paused: 'Paused',
  done: 'Done',
};

// Plain-English meaning of each status, shown in the badge tooltips.
export const STATUS_HELP = {
  active: 'Active — currently sending.',
  pending_approval: 'Pending approval — waiting for you to approve.',
  held: 'Held — the safety system paused it.',
  paused: 'Paused — you paused it.',
  done: 'Done — finished.',
};

// Industry / NAICS options for the New request autocomplete. A small seed list;
// the full list comes from Apollo when this goes live. Shape: { code, label }.
export const NAICS_OPTIONS = [
  { code: '423510', label: 'Metal service centers' },
  { code: '325412', label: 'Pharmaceutical preparation manufacturing' },
  { code: '541511', label: 'Custom computer programming services' },
  { code: '541512', label: 'Computer systems design services' },
  { code: '522110', label: 'Commercial banking' },
  { code: '524114', label: 'Direct health and medical insurance carriers' },
  { code: '621111', label: 'Offices of physicians' },
  { code: '622110', label: 'General medical and surgical hospitals' },
  { code: '561320', label: 'Temporary help services (staffing)' },
  { code: '454110', label: 'Electronic shopping and mail-order houses' },
  { code: '311991', label: 'Perishable prepared food manufacturing' },
  { code: '336111', label: 'Automobile manufacturing' },
  { code: '517311', label: 'Wired telecommunications carriers' },
  { code: '611310', label: 'Colleges and universities' },
  { code: '339112', label: 'Surgical and medical instrument manufacturing' },
];
